import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, inject, Input, OnDestroy, Output, signal } from '@angular/core';
import { Pagination } from '../pagination/pagination';
import { LabelFromValuePipe } from '@shared/pipes/label-from-value.pipe';
import { FEATURE_GROUP_OPTIONS, FEATURE_TYPE_OPTIONS, PLAN_TYPE_OPTIONS } from '@core/constants/options.constants';
import { MasterOption } from '@core/services/master.service';

export interface DataTableColumn {
  key: string;
  label: string;
  width?: string;
  type?: 'text' | 'status' | 'boolean';
}

export interface DataTableAction {
  label: string;
  mode: string;
  variant?: 'default' | 'danger';
}

@Component({
  selector: 'app-data-table',
  imports: [Pagination, LabelFromValuePipe],
  templateUrl: './data-table.html',
  styleUrl: './data-table.scss',
})
export class DataTable<T = object> implements AfterViewInit, OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>);
  private scrollContainer: HTMLElement | null = null;
  private readonly onTableScroll = (): void => this.closeMenu();

  @Input({ required: true }) columns: DataTableColumn[] = [];
  @Input({ required: true }) data: T[] = [];
  @Input() actions: DataTableAction[] = [
    { label: 'View', mode: 'view' },
    { label: 'Edit', mode: 'edit' },
    { label: 'Delete', mode: 'delete', variant: 'danger' },
  ];
  @Input() page = 1;
  @Input() size = 10;
  @Input() totalItems = 0;
  @Input() isLoading = false;
  @Input() isRefreshing = false;
  @Input() featureGroupOptions: MasterOption[] = FEATURE_GROUP_OPTIONS;
  @Input() planTypeOptions: MasterOption[] = PLAN_TYPE_OPTIONS;
  @Input() loadingMessage = 'Loading data...';
  @Input() emptyMessage = 'No records found.';
  @Input() getRowActions?: (row: T) => DataTableAction[];
  @Output() pageChange = new EventEmitter<number>();
  @Output() actionClicked = new EventEmitter<{ mode: string; row: T }>();

  protected readonly featureTypeOptions = FEATURE_TYPE_OPTIONS;
  protected readonly skeletonRows = Array.from({ length: 5 });
  protected readonly openMenuIndex = signal<number | null>(null);
  protected readonly activeRow = signal<T | null>(null);
  protected readonly menuPosition = signal<{ top: number; left: number } | null>(null);

  private readonly menuWidth = 148;
  private readonly menuItemHeight = 36;
  private readonly menuPadding = 8;
  private readonly menuGap = 6;
  private readonly viewportMargin = 8;

  ngAfterViewInit(): void {
    this.scrollContainer = this.host.nativeElement.querySelector('.data-table__scroll');
    this.scrollContainer?.addEventListener('scroll', this.onTableScroll, { passive: true });
  }

  ngOnDestroy(): void {
    this.scrollContainer?.removeEventListener('scroll', this.onTableScroll);
  }

  getValue(row: T, key: string): string | number | boolean {
    const value = (row as Record<string, unknown>)[key];
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }
    return String(value);
  }

  getLabelValue(row: T, key: string): string {
    const value = (row as Record<string, unknown>)[key];
    return typeof value === 'string' ? value : '';
  }

  getStatusClass(row: T, key: string): string {
    const value = String((row as Record<string, unknown>)[key] || '').toLowerCase();
    return value ? `status-${value}` : '';
  }

  toggleMenu(index: number, row: T, event: Event): void {
    event.stopPropagation();

    if (this.openMenuIndex() === index) {
      this.closeMenu();
      return;
    }

    const button = event.currentTarget as HTMLElement;
    const actions = this.getActionsForRow(row);
    const position = this.calculateMenuPosition(button, actions.length);

    this.openMenuIndex.set(index);
    this.activeRow.set(row);
    this.menuPosition.set(position);
  }

  private calculateMenuPosition(
    button: HTMLElement,
    actionCount: number,
  ): { top: number; left: number } {
    const rect = button.getBoundingClientRect();
    const menuHeight = actionCount * this.menuItemHeight + this.menuPadding;
    const left = Math.min(
      Math.max(this.viewportMargin, rect.right - this.menuWidth),
      window.innerWidth - this.menuWidth - this.viewportMargin,
    );

    const spaceBelow = window.innerHeight - rect.bottom - this.menuGap;
    const spaceAbove = rect.top - this.menuGap;
    const openBelow = spaceBelow >= menuHeight || spaceBelow >= spaceAbove;

    const top = openBelow
      ? rect.bottom + this.menuGap
      : Math.max(this.viewportMargin, rect.top - menuHeight - this.menuGap);

    return { top, left };
  }

  closeMenu(): void {
    this.openMenuIndex.set(null);
    this.activeRow.set(null);
    this.menuPosition.set(null);
  }

  onAction(mode: string, row: T, event: Event): void {
    event.stopPropagation();
    this.closeMenu();
    this.actionClicked.emit({ mode, row });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (
      target.closest('.data-table__menu-trigger') ||
      target.closest('.data-table__menu-panel') ||
      target.closest('.drawer-root')
    ) {
      return;
    }
    this.closeMenu();
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onViewportChange(): void {
    this.closeMenu();
  }

  getActionsForRow(row: T): DataTableAction[] {
    return this.getRowActions ? this.getRowActions(row) : this.actions;
  }
}
